import { useTranslator } from '@eo-locale/preact';
import ChevronLeftIcon from 'images/icons/ChevronLeft.svg';
import ChevronRightIcon from 'images/icons/ChevronRight.svg';

import { Button } from 'components/Button';
import { Icon } from 'components/Icon';
import { Stack } from 'components/Stack';
import { Text } from 'components/Text';

import { MAX_PAGES_TO_DISPLAY, paginate } from './utilities';

interface PaginationProps {
  /** Total number of page buttons */
  totalPages: number;
  /** Highlight current page button */
  currentPage: number;
  /** Controls disabled status of the Prev button */
  hasPrev?: boolean;
  /** Controls disabled status of the Next button */
  hasNext?: boolean;
  /** Callback when Prev button is clicked */
  onPrev: () => void;
  /** Callback when Next button is clicked */
  onNext: () => void;
  /** Callback when page button is clicked */
  onPage: (index: number) => void;
}

export function Pagination({
  totalPages,
  currentPage,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onPage,
}: PaginationProps) {
  const { translate } = useTranslator();
  const controlsVisible = totalPages > MAX_PAGES_TO_DISPLAY;
  const { current, pages } = paginate(currentPage, totalPages);

  const pageButtonsMarkup = pages.map((page, index) =>
    typeof page === 'number' ? (
      <Button
        key={`PageButton${page}`}
        compact
        accessibilityLabel={`Go to page ${page}`}
        primary={current === page}
        onClick={() => onPage(page)}
      >
        {page}
      </Button>
    ) : (
      <div
        key={index}
        style={{ display: 'flex', alignItems: 'center', height: '100%' }}
      >
        <Text as="span">...</Text>
      </div>
    ),
  );

  return (
    <Stack spacing="tight">
      {controlsVisible && (
        <Button
          compact
          icon={<Icon source={ChevronLeftIcon} />}
          disabled={!hasPrev}
          accessibilityLabel={translate('reviews_list_previous_page_label', {
            defaultMessage: 'Go to previous page',
          })}
          onClick={onPrev}
        />
      )}
      {pageButtonsMarkup}
      {controlsVisible && (
        <Button
          compact
          icon={<Icon source={ChevronRightIcon} />}
          disabled={!hasNext}
          accessibilityLabel={translate('reviews_list_next_page_label', {
            defaultMessage: 'Go to next page',
          })}
          onClick={onNext}
        />
      )}
    </Stack>
  );
}
